/**
 * sessionKeeper: the page's own last session, kept in memory so a browser that loses its cookie jar cannot sign the officer out.
 *
 * Why (batch 10-30a, ended-offline-truth): when the network returns after an offline spell Chromium can replace its network service
 * process, and a profile without a disk copy of its cookies (private window, a managed profile that does not persist cookies) comes
 * back with an EMPTY jar. Nothing in the page signed out: no SIGNED_OUT event, no request, no write. Every call read after that
 * answers 401, so an ended call showed "Session expired" and never its notice. The session is already in this page's memory (the
 * auth client emits it on every change), so keeping the last one costs nothing.
 *
 * Rules:
 *  - observe() is fed by the single auth client's onAuthStateChange. A SIGNED_OUT forgets the session: an explicit sign-out is never undone.
 *  - restoreSessionIfLost() acts only when the own auth cookie is gone from the jar AND a remembered session exists. It hands the
 *    remembered tokens back through the client's own setSession, so cookie writing and refresh stay in one place. Concurrent calls share
 *    one restore. A refused session (the refresh token was used elsewhere) is left alone: the genuine "session rejected" path reports it.
 *  - A sign-out in another tab through the server form leaves the signed-out marker (lib/auth/signedOutMarker) in the shared jar and
 *    says nothing to this tab: with the marker present the remembered session is forgotten for good, never restored. A new sign-in
 *    (SIGNED_IN, or a page that starts with a session in the jar) removes the marker.
 *  - Only the ended-call gate asks, after the network returned: no other part of the app is changed by this.
 */
import { getAuthStorageKey } from '@/lib/config/environment';
import { clearSignedOutMarker, signedOutMarkerIn } from '@/lib/auth/signedOutMarker';

type RememberedSession = { access_token: string; refresh_token: string };

let remembered: RememberedSession | null = null;
let restoring: Promise<void> | null = null;

/** Fed by the auth client's onAuthStateChange(event, session). */
export function observeAuthState(event: string, session: { access_token?: string; refresh_token?: string } | null): void {
  if (event === 'SIGNED_OUT') { remembered = null; return; }
  if (!session?.access_token || !session.refresh_token) return;
  if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') clearSignedOutMarker();
  remembered = { access_token: session.access_token, refresh_token: session.refresh_token };
}

/** True when the jar holds the own session cookie (or a numbered chunk of it). */
function ownSessionCookiePresent(): boolean {
  if (typeof document === 'undefined') return true;
  const storageKey = getAuthStorageKey();
  return document.cookie.split('; ').some((entry) => {
    const name = entry.split('=')[0];
    return name === storageKey || name.startsWith(`${storageKey}.`);
  });
}

export function restoreSessionIfLost(auth: { setSession(session: RememberedSession): Promise<unknown> }): Promise<void> {
  if (!remembered || ownSessionCookiePresent()) return Promise.resolve();
  // Signed out in another tab (not a dropped jar): never undo it.
  if (typeof document !== 'undefined' && signedOutMarkerIn(document.cookie)) { remembered = null; return Promise.resolve(); }
  const session = remembered;
  restoring ??= (async () => {
    try {
      await auth.setSession(session);
    } catch {
      // Nothing more to do here: a genuine rejection is reported by the existing refresh-failure path.
    } finally {
      restoring = null;
    }
  })();
  return restoring;
}

/** Test seam. */
export function __resetSessionKeeper(): void { remembered = null; restoring = null; }
